import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CountryHighlighter } from '../../countryHighlighter';
import { CameraAnimator } from '../../cameraAnimation';
import { buildBorderLines } from '../../borders';
import { largestPolygonCentroid } from '../../geodata';
import { WorldQuiz } from '../../game';
import { randomBetween } from '../../util';
import { useCountryStore } from '../../store/countryStore';
import { useSceneStore } from '../../store/sceneStore';
import { useToastStore } from '../../store/toastStore';
import { useDebugStore } from '../../store/debugStore';
import { registry } from '../../sceneRegistry';

const EARTH_RADIUS = 10.0;

function Earth({ radius }: { radius: number }) {
    return (
        <group>
            <mesh renderOrder={1}>
                <sphereGeometry args={[radius, 256, 128]} />
                <meshBasicMaterial color={0x111133} />
            </mesh>
            <mesh renderOrder={2}>
                <sphereGeometry args={[radius * 1.05, 64, 32]} />
                <meshBasicMaterial color={0xeeeeff} transparent opacity={0.2} side={THREE.BackSide} />
            </mesh>
        </group>
    );
}

function Stars({ radius, count }: { radius: number; count: number }) {
    const geometry = useMemo(() => {
        const vertices: number[] = [];
        for (let i = 0; i < count; i++) {
            const v = new THREE.Vector3(
                Math.random() * 2 - 1,
                Math.random() * 2 - 1,
                Math.random() * 2 - 1,
            ).normalize().multiplyScalar(randomBetween(0.5 * radius, radius));
            vertices.push(v.x, v.y, v.z);
        }
        const bufferGeometry = new THREE.BufferGeometry();
        bufferGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
        return bufferGeometry;
    }, [radius, count]);

    useEffect(() => () => { geometry.dispose(); }, [geometry]);

    return (
        <points geometry={geometry}>
            <pointsMaterial color={0xffffff} size={2} />
        </points>
    );
}

export function GlobeScene() {
    const { scene, camera: threeCamera, gl } = useThree();
    const camera = threeCamera as THREE.PerspectiveCamera;

    const controlsRef = useRef<OrbitControls | null>(null);
    const highlighterRef = useRef<CountryHighlighter | null>(null);
    const cameraAnimatorRef = useRef<CameraAnimator | null>(null);
    const axesHelperRef = useRef<THREE.AxesHelper | null>(null);
    const borderLinesRef = useRef<THREE.LineSegments | null>(null);
    const initialCameraPositionRef = useRef<THREE.Vector3 | null>(null);
    const initialTargetRef = useRef<THREE.Vector3 | null>(null);
    const countryColorsRef = useRef<Map<string, THREE.Color>>(new Map());

    const debugFrames = useRef(0);
    const debugLastUpdate = useRef(Date.now());
    const isFirstReset = useRef(true);

    const countryData = useCountryStore((state) => state.countryData);
    const fov = useSceneStore((state) => state.fov);
    const showAxes = useSceneStore((state) => state.showAxes);
    const autoRotate = useSceneStore((state) => state.autoRotate);
    const cameraResetVersion = useSceneStore((state) => state.cameraResetVersion);

    // Setup: lighting, controls, imperative objects
    useEffect(() => {
        camera.lookAt(0, 0, 0);
        scene.scale.z = -1;

        const ambientLight = new THREE.AmbientLight(0x404040, 0.3);
        const sun = new THREE.DirectionalLight(0xffffff, 1);
        sun.position.set(1, 1, 1);

        const highlighter = new CountryHighlighter();
        highlighterRef.current = highlighter;

        const axesHelper = new THREE.AxesHelper(50);
        axesHelper.visible = false;
        axesHelperRef.current = axesHelper;

        scene.add(ambientLight, sun, highlighter, axesHelper);

        const controls = new OrbitControls(camera, gl.domElement);
        controls.target.set(0, 0, 0);
        controls.enableDamping = true;
        controls.dampingFactor = 0.1;
        controls.autoRotate = false;
        controls.autoRotateSpeed = 2.0;
        controls.minDistance = 10.1;
        controls.maxDistance = 100;
        controls.rotateSpeed = 0.5;
        controlsRef.current = controls;

        const cameraAnimator = new CameraAnimator(camera, controls);
        cameraAnimatorRef.current = cameraAnimator;

        initialCameraPositionRef.current = camera.position.clone();
        initialTargetRef.current = controls.target.clone();

        return () => {
            scene.remove(ambientLight, sun, highlighter, axesHelper);
            controls.dispose();
            controlsRef.current = null;
            highlighterRef.current = null;
            cameraAnimatorRef.current = null;
            axesHelperRef.current = null;
            scene.scale.z = 1;
        };
    }, [scene, camera, gl]);

    // Country data: border lines + game
    useEffect(() => {
        if (!countryData) return;

        const { lines, countryColors } = buildBorderLines(countryData, EARTH_RADIUS);
        borderLinesRef.current = lines;
        countryColorsRef.current = countryColors;
        scene.add(lines);

        const game = new WorldQuiz(useToastStore.getState().add, countryData);
        registry.game = game;

        game.setOnCorrectGuess((countries) => {
            highlighterRef.current?.addCountries(countries, countryColorsRef.current);
            const local = largestPolygonCentroid(countries, 1.0);
            if (local) {
                cameraAnimatorRef.current?.animateTo(new THREE.Vector3(local.x, local.y, -local.z));
            }
        });

        return () => {
            if (borderLinesRef.current) {
                scene.remove(borderLinesRef.current);
                borderLinesRef.current = null;
            }
            registry.game = null;
        };
    }, [countryData, scene]);

    // FOV watcher
    useEffect(() => {
        camera.fov = fov;
        camera.updateProjectionMatrix();
    }, [fov, camera]);

    // Auto-rotate watcher
    useEffect(() => {
        if (controlsRef.current) controlsRef.current.autoRotate = autoRotate;
        if (cameraAnimatorRef.current) cameraAnimatorRef.current.setDefaultAutoRotate(autoRotate);
    }, [autoRotate]);

    // Axes visibility watcher
    useEffect(() => {
        if (axesHelperRef.current) {
            axesHelperRef.current.visible = showAxes;
        }
    }, [showAxes]);

    // Camera reset watcher
    useEffect(() => {
        if (isFirstReset.current) {
            isFirstReset.current = false;
            return;
        }
        const controls = controlsRef.current;
        const initialPos = initialCameraPositionRef.current;
        const initialTarget = initialTargetRef.current;
        if (!controls || !initialPos || !initialTarget) return;
        camera.position.copy(initialPos);
        controls.target.copy(initialTarget);
        controls.update();
    }, [cameraResetVersion, camera]);

    // Animation loop
    useFrame((state, delta) => {
        const controls = controlsRef.current;
        const cameraAnimator = cameraAnimatorRef.current;
        const highlighter = highlighterRef.current;

        if (cameraAnimator && controls) {
            const animating = cameraAnimator.update(delta);
            if (!animating) {
                controls.update();
                const surfaceDistance = camera.position.length() - EARTH_RADIUS;
                controls.rotateSpeed = THREE.MathUtils.clamp(surfaceDistance / 10, 0.05, 2.0);
            }
        }

        if (highlighter) {
            highlighter.update(state.clock.getElapsedTime());
        }

        // Debug stats: update once per second
        debugFrames.current++;
        const now = Date.now();
        if (now - debugLastUpdate.current >= 1000) {
            useDebugStore.getState().update(
                debugFrames.current,
                state.gl.info.render.calls,
                state.gl.info.render.triangles,
            );
            debugFrames.current = 0;
            debugLastUpdate.current = now;
        }
    });

    return (
        <>
            <Earth radius={EARTH_RADIUS} />
            <Stars radius={1000} count={1000} />
        </>
    );
}
